import React, { useEffect, useRef } from "react";
import gantt from "dhtmlx-gantt";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  extendedProps: {
    status: string;
  };
}

interface TaskGanttProps {
  events: CalendarEvent[];
}

interface GanttTask {
  id: number;
  text: string;
  start_date: Date;
  end_date: Date;
  status: string;
}

const statusColors: Record<string, { bg: string; text: string }> = {
  FAILED: { bg: "#f87171", text: "#ffffff" },
  COMPLETED: { bg: "#22c55e", text: "#ffffff" },
  PENDING: { bg: "#facc15", text: "#000000" },
};

const generateGanttStyles = () =>
  Object.entries(statusColors)
    .map(
      ([status, { bg, text }]) => `
        .gantt-status-${status.toLowerCase()} .gantt_task_content {
          background-color: ${bg} !important;
          color: ${text} !important;
          border-radius: 4px;
          padding: 2px 6px;
          font-size: 12px;
          font-weight: 500;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      `
    )
    .join("\n");

const TaskGantt: React.FC<TaskGanttProps> = ({ events }) => {
  const ganttContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ganttContainer.current) {
      return () => {};
    }

    const tasks: GanttTask[] = events.map((event, i) => {
      const start = new Date(event.start);
      const end = new Date(start);
      end.setDate(start.getDate() + 1);

      return {
        id: i + 1,
        text: event.title,
        start_date: start,
        end_date: end,
        status: event.extendedProps.status ?? "UNKNOWN",
      };
    });

    if (tasks.length === 0) {
      gantt.clearAll();
      return () => {};
    }

    const dates = tasks.map((t) => t.start_date.getTime());
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    maxDate.setDate(maxDate.getDate() + 1);

    gantt.clearAll();
    gantt.config.readonly = true;
    gantt.config.start_date = minDate;
    gantt.config.end_date = maxDate;
    gantt.config.scale_unit = "day";
    gantt.config.date_scale = "%d %M";
    gantt.config.min_column_width = 120;
    gantt.config.columns = [
      {
        name: "text",
        label: "Task",
        tree: true,
        width: 180,
        template: (task: GanttTask) =>
          `<span title="${task.text}">${task.text}</span>`,
      },
      {
        name: "start_date",
        label: "Date",
        align: "center",
        width: 150,
        template: (task: GanttTask) => {
          return task.start_date.toLocaleString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },
    ];

    gantt.templates.task_text = (_start, _end, task: GanttTask) => {
      const color = statusColors[task.status] || "#6b7280";
      return `
    <div style="display: flex; flex-direction: column;">
      <span style="font-weight: 600;">${task.text}</span>
      <span style="font-size: 10px; color: ${color};">${task.status}</span>
    </div>
  `;
    };

    gantt.templates.tooltip_text = (start, end, task: GanttTask) => {
      const format = gantt.date.date_to_str("%d %M %Y %H:%i");
      return `<b>${task.text}</b><br/>Start: ${format(start)}<br/>End: ${format(
        end
      )}<br/>Status: ${task.status}`;
    };
    gantt.templates.task_class = (_start, _end, task: GanttTask) =>
      `gantt-status-${task.status?.toLowerCase()}`;

    gantt.init(ganttContainer.current);
    gantt.parse({ data: tasks });

    return () => {
      gantt.clearAll();
    };
  }, [events]);

  return (
    <>
      <style>{generateGanttStyles()}</style>
      <div
        ref={ganttContainer}
        className="w-full overflow-x-auto"
        style={{ height: "calc(100vh - 90px)" }}
      />
    </>
  );
};

export default TaskGantt;
